import CommonTable from '../../../../components/common-table/CommonTable';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import moment from '../../../../common/constants/moment';
import { commonDateDotsFormat } from '../../../../common/constants/dateConstants';
import { ArrowRightOutlined } from '@ant-design/icons';
import { setCurrentClientOnboardingContract } from '../../../../redux/reducers/onboarding/sliceReducer';
import { IEmployeeDto } from '../../../../common/api/services/UserApi/types/IEmployeeDto';
import { compareBeforeDates } from '../../../../common/utils/compareEndDate';
import { FieldKeys } from '../../../../common/api/services/HistoryApi/types/fieldKeys';
import { Roles } from '../../../../common/enums/Roles';

const OfficePrefix = {
    Zurich: 'NQZH',
    Zug: 'NQZG',
    Geneva: 'NQGE',
};

interface IProps {
    sellersList: IEmployeeDto[];
}

const WorkContactClientsList = (props: IProps) => {
    const { sellersList } = props;
    const dispatch = useAppDispatch();
    const { workContract, clientContract, validationError } = useAppSelector((state) => state.onboarding);
    const activeFields = useAppSelector((state) => state.onBoardingHistoryWC.activeFields);
    const errorMessages: any = useAppSelector((state) => state.onBoardingHistory.errorMessages);
    const invoices = activeFields[FieldKeys.invoiceCurrency];
    const { companies } = workContract;
    const { selectedClientIdx } = workContract;
    const { role } = useAppSelector((state) => state.auth);
    const isAdmin = Roles.ADMIN === role || Roles.MANAGER === role;
    const today = new Date();
    const findActiveInvoice = (companyId: string) => {
        return invoices?.find((invoice) => {
            if (invoice.companyId !== companyId) return false;

            const startDate = invoice.startDate ? new Date(String(invoice.startDate)) : null;
            const endDate = invoice.endDate ? new Date(String(invoice.endDate)) : null;

            return startDate && today >= startDate && (!endDate || today <= endDate);
        });
    };

    const getClient = (company: any) => {
        return clientContract.clients.find((item) => item.companyId === company._id);
    };

    const checkTab = (index: number, id: string) => {
        return (validationError.workContract &&
            Object.keys(validationError.workContract).some((error) => error.startsWith(`data.companies[${index}]`))) ||
            errorMessages?.find((msg: any) => msg.id === id)
            ? 'error-row'
            : '';
    };

    const data = companies.map((company, idx) => {
        const seller = sellersList?.find((item) => item._id === company.seller);
        const isExpired = company?.endDate ? compareBeforeDates(company?.endDate) : false;
        const activeInvoice = findActiveInvoice(company._id);
        const invoiceCurrency = activeInvoice ? activeInvoice.invoiceCurrency : company.invoiceCurrency;
        return {
            name: company.name || '-',
            startDate: company.startDate ? moment(company.startDate).format(commonDateDotsFormat) : '',
            endDate: getClient(company)?.endDate
                ? moment(getClient(company)?.endDate).format(commonDateDotsFormat)
                : '',
            endDateConsultant: company.endDate ? moment(company.endDate).format(commonDateDotsFormat) : '',
            payRate: company.rate,
            invoiceCurrency,
            seller: seller ? `${seller.firstName} ${seller.lastName}` : '',
            clientIndex: idx,
            entity: company?.numeriqOffice ? OfficePrefix[company.numeriqOffice as keyof typeof OfficePrefix] : '',
            billRate: getClient(company)?.billRate || '',
            agency: getClient(company)?.agencyName || '',
            isExpired,
            _id: company._id,
        };
    });

    const onSelectClient = (selectedIdx: number) => {
        if (selectedIdx === selectedClientIdx) {
            return;
        }
        dispatch(setCurrentClientOnboardingContract(selectedIdx));
    };

    const columns = [
        {
            title: 'Entity',
            dataIndex: 'entity',
            width: 80,
        },
        {
            title: 'Client',
            dataIndex: 'name',
            width: 150,
        },
        {
            title: 'Start date',
            dataIndex: 'startDate',
            width: 120,
        },
        {
            title: 'End date consultant',
            dataIndex: 'endDateConsultant',
            width: 120,
        },
        {
            title: 'End date client',
            dataIndex: 'endDate',
            width: 120,
        },
        {
            title: 'Bill rate',
            dataIndex: 'billRate',
            width: 80,
        },
        {
            title: 'Pay rate',
            dataIndex: 'payRate',
            width: 80,
        },
        {
            title: 'Invoice currency',
            dataIndex: 'invoiceCurrency',
            width: 120,
        },
        {
            title: 'Agency',
            dataIndex: 'agency',
            width: 120,
        },
        {
            title: 'Seller',
            dataIndex: 'seller',
            width: 150,
        },
        // {
        //     title: '',
        //     dataIndex: 'action',
        //     width: 50,
        //     render: (text: string, data: any) => (
        //         <div onClick={() => onSelectClient(data.clientIndex)}>
        //             <ArrowRightOutlined />
        //         </div>
        //     ),
        // },
    ];
    return (
        <CommonTable
            data={data}
            columns={
                isAdmin
                    ? columns
                    : columns.filter((item) =>
                          [
                              'name',
                              'startDate',
                              'endDate',
                              'endDateConsultant',
                              'payRate',
                              'invoiceCurrency',
                              'action',
                          ].includes(item.dataIndex)
                      )
            }
            hidePagination
            height={150}
            isBorderedTable
            tableHeight={150}
            onRowClick={(record) => onSelectClient(record.clientIndex)}
            rowClassName={(record) => {
                if (record.isExpired && record.clientIndex === selectedClientIdx) {
                    return 'selected-row';
                }
                if (record.isExpired && checkTab(record.clientIndex, record._id)) {
                    return 'error-row-expired-selected';
                }
                if (record.isExpired) {
                    return 'error-row-expired';
                }

                if (
                    (record.isExpired || checkTab(record.clientIndex, record._id)) &&
                    record.clientIndex === selectedClientIdx
                ) {
                    return 'error-selected-row';
                }

                return record.clientIndex === selectedClientIdx
                    ? 'selected-row'
                    : checkTab(record.clientIndex, record._id);
            }}
        />
    );
};

export default WorkContactClientsList;
